import type { Document, Block, Inline, Text } from "@contentful/rich-text-types"
import type { Asset } from "next-video/dist/assets.js"
import type { SupportingMaterialsMap as SharedSupportingMaterialsMap } from "domains/contentful/media"

export type CourseSeries = string
export type CourseTier = "free" | "premium"

export type SupportingMaterialsMap = SharedSupportingMaterialsMap

export interface RecordingStripePrice<T = string> {
    individual?: T | null
    organization?: T | null
}

export type RecordingRichText = {
    json?: Document | null
}

export type RecordingRichTextNode = Block | Inline | Text

export interface RecordingDetails<T = string> {
    fileName?: string | null
    blurb?: string | null
    about?: string | null
    aboutRichText?: RecordingRichText | null
    durationSeconds?: number | null
    publishedAt?: Date | null
    presenters?: string[]
    supportingMaterials?: SupportingMaterialsMap
    stripe?: {
        price: RecordingStripePrice<T>
    }
    transcriptUrl?: string | null
    muxAsset?: Asset | null
    chapters?: Array<{
        title: string
        startTime: number
    }>
}

export interface LiveStripePrice<T = string> {
    individual?: T | null
    organization?: T | null
}

export interface LiveDetails<T = string> {
    stripe?: {
        price: LiveStripePrice<T>
    }
    ticketTailor?: {
        url?: string | null
    }
    presenters?: string[]
    level?: string | null
    durationMinutes?: number | null
    utcTimestamp?: number | null
    blurb?: string | null
}

export interface CourseMeta<T = string> {
    series: CourseSeries
    title: string
    tier: CourseTier
    featured?: boolean
    summary?: string | null
    longDescription?: string | null
    longDescriptionRichText?: RecordingRichText | null
    episodeNumber?: number | null
    talkingAiCategory?: boolean
    contentCategory?: string | null
    recording?: RecordingDetails<T>
    live?: LiveDetails<T>
    thumbnail?: {
        url?: string | null
        blurDataUrl?: string | null
    }
    linkedCourse?: CourseSeries | null
}

export function isFreeCourse<T>(course: CourseMeta<T>): course is CourseMeta<T> & {
    tier: "free"
    recording: RecordingDetails<T>
} {
    return course.tier === "free" && !!course.recording
}

export function isPremiumCourse<T>(course: CourseMeta<T>): course is CourseMeta<T> & {
    tier: "premium"
} {
    return course.tier === "premium"
}

export function hasRecording<T>(course: CourseMeta<T>): course is CourseMeta<T> & {
    recording: RecordingDetails<T>
} {
    return Boolean(course.recording)
}

export function hasLive<T>(course: CourseMeta<T>): course is CourseMeta<T> & {
    live: LiveDetails<T>
} {
    return Boolean(course.live)
}

export function sortCoursesByRecordingPublishedAt<T>(courses: Array<CourseMeta<T>>): Array<CourseMeta<T>> {
    return [...courses].sort((a, b) => {
        const aDate = a.recording?.publishedAt?.getTime() ?? 0
        const bDate = b.recording?.publishedAt?.getTime() ?? 0
        return bDate - aDate
    })
}

export function sortCoursesByLiveUtcTimestamp<T>(courses: Array<CourseMeta<T>>): Array<CourseMeta<T>> {
    return [...courses].sort((a, b) => {
        const aDate = a.live?.utcTimestamp ? a.live.utcTimestamp * 1000 : 0
        const bDate = b.live?.utcTimestamp ? b.live.utcTimestamp * 1000 : 0
        return bDate - aDate
    })
}

export function sortCoursesByDate<T>(courses: Array<CourseMeta<T>>): Array<CourseMeta<T>> {
    return sortCoursesByRecordingPublishedAt(courses).sort((a, b) => {
        const aTime = a.recording?.publishedAt?.getTime() ?? a.live?.utcTimestamp ?? 0
        const bTime = b.recording?.publishedAt?.getTime() ?? b.live?.utcTimestamp ?? 0
        return bTime - aTime
    })
}

export function sortSourcesRecordsByDate<T extends CourseMeta<any>, TKey extends string = CourseSeries>(
    sources: Record<TKey, T>
): Record<TKey, T> {
    const entries = Object.entries(sources) as Array<[TKey, T]>

    entries.sort(([, courseA], [, courseB]) => {
        const aDate = courseA.recording?.publishedAt?.getTime() ?? 0
        const bDate = courseB.recording?.publishedAt?.getTime() ?? 0
        return bDate - aDate
    })

    return Object.fromEntries(entries) as Record<TKey, T>
}

export type CourseWithMeta<T = string> = CourseMeta<T> & {
    access?: boolean | undefined | null
    priority?: boolean
}
